import { Bug, Shield, Code2, ShieldAlert, FileCode, Filter } from 'lucide-react';
import type { AnalysisIssue } from '../../../entities/issue';
import { Badge } from '../../../shared/ui';

interface IssueRowProps {
  issue: AnalysisIssue;
  onClick?: () => void;
  className?: string;
}

const issueIcons: Record<string, React.ReactNode> = {
  'bug': <Bug className="h-4 w-4" />,
  'vulnerability': <Shield className="h-4 w-4" />,
  'code_smell': <Code2 className="h-4 w-4" />,
  'security_hotspot': <ShieldAlert className="h-4 w-4" />,
  'code_quality': <Filter className="h-4 w-4" />,
};

const severityBorderColors: Record<string, string> = {
  'critical': 'border-l-primary',
  'high': 'border-l-border-light',
  'medium': 'border-l-border',
  'low': 'border-l-border',
};

const severityMap: Record<string, 'critical' | 'high' | 'medium' | 'low'> = {
  'critical': 'critical',
  'high': 'high',
  'medium': 'medium',
  'low': 'low',
};

export function IssueRow({ issue, onClick, className = '' }: IssueRowProps) {
  return (
    <div
      onClick={onClick}
      className={`
        grid grid-cols-[180px_100px_250px_1fr] gap-4 items-center
        rounded-asymmetric-md border border-border bg-surface p-4
        border-l-4 ${severityBorderColors[issue.severity] || severityBorderColors['low']}
        hover:border-primary hover:translate-x-1 hover:shadow-glow
        transition-all duration-300
        ${onClick ? 'cursor-pointer' : ''}
        ${className}
      `}
    >
      {/* Type */}
      <div className="flex items-center gap-2 text-text-primary font-mono text-sm">
        {issueIcons[issue.type] || <Code2 className="h-4 w-4" />}
        <span>{issue.type}</span>
      </div>

      {/* Severity Badge */}
      <div>
        <Badge severity={severityMap[issue.severity] || 'low'} size="sm" corners="asymmetric">
          {issue.severity}
        </Badge>
      </div>

      {/* File Path */}
      <div className="flex items-center gap-2 text-text-muted font-mono text-sm truncate">
        <FileCode className="h-4 w-4 flex-shrink-0" />
        <span className="truncate">
          {issue.file_path}:{issue.line_start}
        </span>
      </div>

      {/* Message */}
      <div className="text-text-secondary text-sm truncate">
        {issue.description}
      </div>
    </div>
  );
}
